import React from "react";
import Image from "next/image";
import {
  ButtonsContainer,
  ClearFloat,
  Container,
  Excerpt,
  Heading,
  ImageSpacer,
  ImageWrapper,
  Podium,
} from "./styles";
import InternalLink, { InternalLinkProps } from "./InternalLink/InternalLink";
import ExternalLink from "./ExternalLink";
import CallToAction from "@/components/CallToAction";

type InternalLinkButton = InternalLinkProps & {
  _type: "internalLink";
  title: string;
};

type ExternalLinkButton = {
  _type: "externalLink";
  title: string;
  url: string;
  blank: boolean;
};

type DownloadLinkButton = {
  _type: "downloadLink";
  title: string;
  file: {
    asset: {
      url: string;
    };
  };
};

type ProductSearchButton = {
  _type: "object";
  title: string;
  params: string;
  page: {
    _type: string;
    slug: string;
  };
};

type CallToActionButton = {
  _type: "callToAction";
  icon: string;
  variant: string;
} & (
  | { action: "internalLink"; internalLink: Omit<InternalLinkButton, "_type"> }
  | { action: "externalLink"; externalLink: Omit<ExternalLinkButton, "_type"> }
  | { action: "downloadLink"; downloadLink: Omit<DownloadLinkButton, "_type"> }
  | {
      action: "productSearch";
      productSearch: Omit<ProductSearchButton, "_type">;
    }
);

type Button = { _key: string } & (
  | InternalLinkButton
  | ExternalLinkButton
  | CallToActionButton
);

type Props = {
  heading: string | null;
  excerpt: string | null;
  buttons: Button[] | null;
  image: {
    asset: {
      url: string;
    };
    alt: string | null;
    position: string | null;
  } | null;
};

/**
 * @see https://www.figma.com/file/16YSFRr06xdFYJFqNgvv41/Bormioli-Product-Design?node-id=602%3A61637
 **/
const TextPodiumWithImage: React.FC<Props> = (props) => {
  const { heading, excerpt, buttons, image } = props;

  return (
    <Container>
      <Podium>
        {heading && <Heading>{heading}</Heading>}
        {excerpt && <Excerpt>{excerpt}</Excerpt>}
        {buttons && buttons.length && (
          <>
            <ButtonsContainer>
              {buttons?.map((button) => {
                if (!button?._key) return null;
                switch (button._type) {
                  case "internalLink":
                    return <InternalLink key={button._key} {...button} />;
                  case "externalLink":
                    return <ExternalLink key={button._key} {...button} />;
                  case "callToAction":
                    const commonProps = {
                      as: "a" as "a",
                      key: button._key,
                      icon: button.icon as any,
                      variant: button.variant as any,
                    };
                    if (button.action === "internalLink") {
                      return (
                        <CallToAction
                          {...commonProps}
                          action="internalLink"
                          internalLink={button.internalLink}
                        />
                      );
                    } else if (button.action === "externalLink") {
                      return (
                        <CallToAction
                          {...commonProps}
                          action="externalLink"
                          externalLink={button.externalLink}
                        />
                      );
                    } else if (button.action === "downloadLink") {
                      return (
                        <CallToAction
                          {...commonProps}
                          action="downloadLink"
                          downloadLink={button.downloadLink}
                        />
                      );
                    } else if (button.action === "productSearch") {
                      return (
                        <CallToAction
                          {...commonProps}
                          action="productSearch"
                          productSearch={button.productSearch}
                        />
                      );
                    }
                    return null;
                  default:
                    return null;
                }
              })}
            </ButtonsContainer>
            <ClearFloat />
          </>
        )}
      </Podium>
      {image?.asset?.url && (
        <>
          <ImageSpacer />
          <ImageWrapper>
            <Image
              src={image?.asset?.url}
              layout="fill"
              objectFit="cover"
              alt={image?.alt || ""}
              priority
            />
          </ImageWrapper>
        </>
      )}
    </Container>
  );
};

export default TextPodiumWithImage;
